(() => {
  const DATA = window.CanteinFiberData;
  const app = document.querySelector('[data-fc-app]');
  if (!DATA || !app) return;

  const $ = (selector) => app.querySelector(selector);
  const form = $('[data-fc-form]');
  const logEl = $('[data-fc-log]');
  const outputEl = $('[data-fc-output]');
  const summaryEl = $('[data-fc-summary]');
  const searchEl = $('#fc-food-search');
  const optionsEl = $('#fc-food-options');
  const mealNameEl = $('[data-fc-meal-name]');

  const ANALYTICS_KEY = app.dataset.analyticsKey || '';
  const ANALYTICS_HOST = (app.dataset.analyticsHost || 'https://us.i.posthog.com').replace(/\/+$/, '');
  const VISIT_ID = window.crypto?.randomUUID?.() || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  const MISS_DELAY = 2000;
  const STORAGE_KEY = 'cantein-fiber-calculator-v1';
  const METER_MAX = 1.25;
  const MET = 0.98;
  const STEP = 5;
  // Fiber per 1,000 calories logged only means something once most of the day is logged.
  const COMPLETE_DAY = 0.7;
  const PER_1000_KCAL = 14;
  const US_AVERAGE = 17;
  const DAILY_VALUE = 28;
  const FOODS = DATA.foods;
  const FOOD = Object.fromEntries(FOODS.map((food) => [food.id, food]));

  const MEALS = { breakfast: 'Breakfast', lunch: 'Lunch', dinner: 'Dinner', snacks: 'Snacks' };
  const CHOICES = {
    sex: ['male', 'female'],
    stage: ['none', 'pregnant', 'breastfeeding'],
    meal: Object.keys(MEALS),
  };
  const FIELDS = ['sex', 'age', 'stage', 'calories', 'meal'];
  const ITEM_FIELDS = ['qty', 'unit', 'fiber'];
  const DEFAULTS = { sex: 'female', age: '', stage: 'none', calories: '', meal: 'breakfast' };
  const SAMPLES = {
    typical: {
      profile: { sex: 'female', age: '42' },
      items: [
        ['egg', 2, 'breakfast'], ['bread-whole-grain', 2, 'breakfast'], ['banana', 1, 'breakfast'],
        ['turkey-deli', 3, 'lunch'], ['bread-white', 2, 'lunch'], ['apple', 1, 'lunch'],
        ['chicken-breast', 4, 'dinner'], ['white-rice', 1, 'dinner'], ['broccoli', 0.5, 'dinner'],
        ['greek-yogurt', 1, 'snacks'],
      ],
    },
    high: {
      profile: { sex: 'male', age: '46' },
      items: [
        ['oats', 0.5, 'breakfast'], ['raspberries', 0.5, 'breakfast'], ['almond-milk', 1, 'breakfast'],
        ['lentils', 0.5, 'lunch'], ['quinoa', 1, 'lunch'], ['spinach', 2, 'lunch'],
        ['salmon', 4, 'dinner'], ['sweet-potato', 1, 'dinner'], ['broccoli', 1, 'dinner'],
        ['pear', 1, 'snacks'],
      ],
    },
  };

  let state = loadState();
  let nextUid = state.items.length + 1;
  let options = [];
  let activeOption = -1;
  let saveTimer = 0;

  const esc = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => `&#${char.charCodeAt(0)};`);
  const num = (value) => {
    const n = parseFloat(String(value ?? '').replace(',', '.'));
    return Number.isFinite(n) ? n : NaN;
  };
  const round = (value, digits = 0) => Math.round(value * 10 ** digits) / 10 ** digits;
  const fmtNum = (value, digits = 0) => round(value, digits).toLocaleString('en-US', { maximumFractionDigits: digits });
  const fmtFiber = (grams) => (grams < 9.95 ? grams.toFixed(1).replace(/\.0$/, '') : fmtNum(grams));
  const fmtPct = (ratio) => `${Math.round(ratio * 100)}%`;
  const FRACTIONS = { 0.25: '¼', 0.5: '½', 0.75: '¾' };

  function fmtQty(qty) {
    const whole = Math.floor(qty + 1e-6);
    const part = round(qty - whole, 2);
    if (part === 0) return String(whole);
    if (FRACTIONS[part]) return `${whole || ''}${FRACTIONS[part]}`;
    return fmtNum(qty, 2);
  }

  const servingLabel = (label, grams) => (/\d/.test(label) ? label : `${label} (${fmtNum(grams)} g)`);

  function amountLabel(food, { qty, unit }) {
    if (unit === 'g') return `${fmtNum(qty, 1)} g`;
    const [label, size, plural] = food.s[unit];
    const name = (qty > 1 + 1e-6 ? plural || label : label).replace(/\s*\(.*\)$/, '');
    return `${fmtQty(qty)} ${name} (${fmtNum(qty * size)} g)`;
  }

  const portion = (food, grams) => amountLabel(food, { qty: grams / food.s[0][1], unit: 0 });

  function sampleItems(name, firstUid = 1) {
    return SAMPLES[name].items.map(([foodId, qty, meal], i) => ({ uid: firstUid + i, foodId, qty: String(qty), unit: 0, meal }));
  }

  // First visits open on the sample day so the results show straight away.
  function exampleState() {
    return { ...DEFAULTS, ...SAMPLES.typical.profile, items: sampleItems('typical') };
  }

  function loadState() {
    const fresh = { ...DEFAULTS, items: [] };
    try {
      const saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
      if (!saved || typeof saved !== 'object') return exampleState();
      for (const field of FIELDS) {
        if (typeof saved[field] !== 'string') continue;
        if (CHOICES[field] && !CHOICES[field].includes(saved[field])) continue;
        fresh[field] = saved[field];
      }
      if (!fresh.age.trim()) fresh.age = SAMPLES.typical.profile.age;
      const items = Array.isArray(saved.items) ? saved.items.filter(isValidItem) : [];
      fresh.items = items.map((item, i) => ({ ...item, uid: i + 1 }));
    } catch {
      return exampleState();
    }
    return fresh;
  }

  function isValidItem(item) {
    if (!item || !MEALS[item.meal]) return false;
    if (item.custom) return typeof item.name === 'string';
    const food = FOOD[item.foodId];
    return Boolean(food) && (item.unit === 'g' || (Number.isInteger(item.unit) && item.unit >= 0 && item.unit < food.s.length));
  }

  function save() {
    clearTimeout(saveTimer);
    saveTimer = setTimeout(() => {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      } catch {
        // Storage can be unavailable (private browsing); the calculator still works for this visit.
      }
    }, 250);
  }

  // Institute of Medicine adequate intakes (2005), in grams a day.
  function adequateIntake(sex, age, stage) {
    if (sex === 'female' && stage === 'pregnant') return { grams: 28, label: 'pregnancy' };
    if (sex === 'female' && stage === 'breastfeeding') return { grams: 29, label: 'breastfeeding' };
    if (sex === 'male') return age < 51 ? { grams: 38, label: 'men under 51' } : { grams: 30, label: 'men 51 and older' };
    if (age < 19) return { grams: 26, label: 'women 18 and under' };
    return age < 51 ? { grams: 25, label: 'women under 51' } : { grams: 21, label: 'women 51 and older' };
  }

  function getProfile() {
    const age = num(state.age);
    if (!(age > 0)) return { error: 'Enter your age to see your fiber target.', missingAge: true };
    if (age < 18) return { error: 'This calculator is designed for adults 18 and older.' };
    if (age > 110) return { error: 'Check your age.' };
    const sex = state.sex === 'male' ? 'male' : 'female';
    const intake = adequateIntake(sex, age, state.stage);
    const caloriesInput = num(state.calories);
    const calories = caloriesInput >= 1000 && caloriesInput <= 5000 ? caloriesInput : null;
    const target = calories ? round((PER_1000_KCAL * calories) / 1000) : intake.grams;
    return {
      sex,
      age,
      intake,
      calories,
      // The daily calories the table number is built on, to the nearest 100.
      assumedCalories: Math.round((intake.grams * 1000) / PER_1000_KCAL / 100) * 100,
      caloriesIgnored: String(state.calories).trim() !== '' && calories === null,
      target,
    };
  }

  function itemGrams(item, food) {
    const qty = Math.max(0, num(item.qty) || 0);
    return item.unit === 'g' ? qty : qty * food.s[item.unit][1];
  }

  function nutrients(item) {
    if (item.custom) return { fiber: Math.max(0, num(item.fiber) || 0), kcal: null };
    const food = FOOD[item.foodId];
    const factor = itemGrams(item, food) / 100;
    return { fiber: food.f * factor, kcal: food.k * factor };
  }

  function analyze(profile) {
    const meals = Object.fromEntries(Object.keys(MEALS).map((key) => [key, { fiber: 0, count: 0 }]));
    let fiber = 0;
    let kcal = 0;
    let kcalKnown = true;
    const sources = [];
    for (const item of state.items) {
      const n = nutrients(item);
      fiber += n.fiber;
      if (n.kcal === null) kcalKnown = false;
      else kcal += n.kcal;
      meals[item.meal].fiber += n.fiber;
      meals[item.meal].count += 1;
      if (n.fiber > 0) sources.push({ item, fiber: n.fiber });
    }
    sources.sort((a, b) => b.fiber - a.fiber);
    const deficit = Math.max(0, profile.target - fiber);
    let status = 'ok';
    if (fiber < profile.target * MET) status = deficit > 2 * STEP ? 'low' : 'short';
    return {
      fiber,
      kcal,
      density: kcalKnown && kcal > 0 ? (fiber / kcal) * 1000 : null,
      meals,
      sources,
      deficit,
      status,
      ratio: fiber / profile.target,
    };
  }

  // Foods that add about one step of fiber within a realistic portion, best fiber per calorie first.
  function nextStep(analysis) {
    const goal = Math.min(STEP, analysis.deficit);
    const logged = new Set(state.items.map((item) => item.foodId));
    const rows = FOODS
      .filter((food) => food.x > 0 && food.f > 0 && !logged.has(food.id))
      .map((food) => {
        const perGram = food.f / 100;
        const step = food.s[0][1] * food.r;
        const maxGrams = Math.floor(food.x / step + 1e-6) * step;
        const needed = Math.max(step, Math.ceil((goal * 0.9) / perGram / step - 1e-6) * step);
        const grams = Math.min(needed, maxGrams);
        return {
          food,
          grams,
          closes: needed <= maxGrams + 1e-6,
          fiber: perGram * grams,
          kcal: (food.k * grams) / 100,
          density: food.f / food.k,
        };
      });
    rows.sort((a, b) => Number(b.closes) - Number(a.closes) || b.density - a.density);
    const perGroup = {};
    return {
      goal,
      rows: rows
        .filter(({ food }) => {
          perGroup[food.g] = (perGroup[food.g] || 0) + 1;
          return perGroup[food.g] <= 2;
        })
        .slice(0, 6),
    };
  }

  function meter(ratio, status, label) {
    const at = (value) => `${((Math.min(value, METER_MAX) / METER_MAX) * 100).toFixed(1)}%`;
    return `<div class="cantein-aa__meter" role="img" aria-label="${esc(label)}">`
      + `<span class="cantein-aa__meter-fill is-${status}" style="--fill: ${at(ratio)}"></span>`
      + `<span class="cantein-aa__meter-mark" style="--at: ${at(1)}"></span></div>`;
  }

  const panel = (title, body, className = '') => `<div class="cantein-aa__panel${className ? ` ${className}` : ''}"><h3>${title}</h3>${body}</div>`;

  function addButton(food, grams, className) {
    const label = `Add ${portion(food, grams)} of ${food.n} to ${MEALS[state.meal].toLowerCase()}`;
    return `<button type="button" class="${className}" data-add-food="${esc(food.id)}" data-grams="${round(grams, 2)}" aria-label="${esc(label)}">Add</button>`;
  }

  function targetPanel(profile, analysis) {
    let basis;
    if (profile.calories) {
      basis = `${PER_1000_KCAL} g per 1,000 calories × ${fmtNum(profile.calories)} calories. The Institute of Medicine table number for ${profile.intake.label} is ${profile.intake.grams} g.`;
    } else {
      basis = `The Institute of Medicine adequate intake for ${profile.intake.label}, which assumes about ${fmtNum(profile.assumedCalories)} calories a day. Eat more or less than that? Add your daily calories to scale it at ${PER_1000_KCAL} g per 1,000.`;
      if (profile.caloriesIgnored) basis += ' Calories are only used between 1,000 and 5,000.';
    }
    if (state.items.length) {
      if (analysis.status !== 'ok') basis += ` You’re ${fmtFiber(analysis.deficit)} g short today.`;
      else basis += analysis.deficit > 0 ? ' Within 1 g of target today.' : ' Target met today.';
    }
    const label = `${fmtNum(analysis.fiber)} of ${fmtNum(profile.target)} grams of fiber, ${fmtPct(analysis.ratio)} of target`;
    return panel('Daily fiber target', `
      <div class="cantein-aa__protein-top">
        <p class="cantein-aa__big">${fmtNum(profile.target)}<small>g / day</small></p>
        <dl class="cantein-aa__stats">
          <dt>Eaten today</dt><dd>${fmtFiber(analysis.fiber)} g</dd>
          <dt>US adult average</dt><dd>${US_AVERAGE} g</dd>
          <dt>Daily Value</dt><dd>${DAILY_VALUE} g</dd>
        </dl>
      </div>
      ${meter(analysis.ratio, analysis.status, label)}
      <p class="cantein-aa__panel-note">${esc(basis)}</p>`, 'cantein-aa__protein');
  }

  function rampPanel(profile, analysis) {
    if (analysis.status === 'ok') {
      return panel('Your pace', `<p class="cantein-aa__good">You’re at your target. More isn’t automatically better, so there’s no need to keep climbing.</p>`);
    }
    const start = analysis.fiber;
    const weeks = Math.ceil(analysis.deficit / STEP - 1e-6);
    const steps = Array.from({ length: weeks }, (_, i) => Math.min(profile.target, start + STEP * (i + 1)));
    const rows = steps.map((grams, i) => `
      <li class="cantein-aa__gap is-step">
        <span class="cantein-aa__gap-rank">${i + 1}</span>
        <div><strong>Week ${i + 1}</strong><p>About ${fmtNum(grams)} g a day${i === steps.length - 1 ? ' · your target' : ''}</p></div>
        <span class="cantein-aa__gap-status">+${fmtFiber(grams - (i ? steps[i - 1] : start))} g</span>
      </li>`).join('');
    const intro = weeks === 1
      ? `You’re one step away. Add about ${fmtFiber(analysis.deficit)} g a day and hold there.`
      : `Add about ${STEP} g a day, then hold for a week before the next step. From ${fmtFiber(start)} g, that’s ${weeks} weeks to ${fmtNum(profile.target)} g.`;
    return panel('Your pace: 5 g a week', `
      <p>${intro}</p>
      <ul class="cantein-aa__gaps">${rows}</ul>
      <p class="cantein-aa__panel-note">Drink more water as you go. If you get gassy or bloated, drop back to the last step that felt fine and stay there another week.</p>`);
  }

  function nextPanel(analysis) {
    if (analysis.status === 'ok') return '';
    const { goal, rows } = nextStep(analysis);
    if (!rows.length) return '';
    const list = rows.map(({ food, grams, closes, fiber, kcal }) => `
      <li>
        <div><strong>${esc(food.n)}</strong><span class="cantein-aa__tag${closes ? '' : ' is-partial'}">+${fmtFiber(fiber)} g fiber</span></div>
        <span>${esc(portion(food, grams))} · ${fmtNum(kcal)} kcal</span>
        ${addButton(food, grams, 'cantein-aa__food-add')}
      </li>`).join('');
    return panel(`Your next ${fmtFiber(goal)} g`, `
      <p>Each of these adds about ${fmtFiber(goal)} g on its own, ranked by fiber per calorie. Pick one, not all of them. Tap Add to log it to ${MEALS[state.meal].toLowerCase()}.</p>
      <ul class="cantein-aa__foods">${list}</ul>`);
  }

  function sourcesPanel(profile, analysis) {
    if (!analysis.sources.length) {
      return panel('Where today’s fiber came from', '<p>Nothing you’ve logged has fiber yet. Meat, fish, eggs, and dairy have none, so it all comes from plants.</p>');
    }
    const rows = analysis.sources.slice(0, 5).map(({ item, fiber }) => {
      const name = item.custom ? item.name || 'Other food' : FOOD[item.foodId].n;
      const share = analysis.fiber > 0 ? fiber / analysis.fiber : 0;
      return `<li><div><strong>${esc(name)}</strong><span>${MEALS[item.meal]}</span></div><span class="cantein-aa__plan-stat">${fmtFiber(fiber)} g · ${fmtPct(share)}</span></li>`;
    }).join('');
    const meals = Object.entries(MEALS)
      .filter(([key]) => analysis.meals[key].count)
      .map(([key, label]) => `${label} ${fmtFiber(analysis.meals[key].fiber)} g`)
      .join(' · ');
    const dayCalories = profile.calories || profile.assumedCalories;
    let density = '';
    if (analysis.density !== null && analysis.kcal >= dayCalories * COMPLETE_DAY) {
      density = `<p class="cantein-aa__plan-after">Your day works out to ${fmtNum(analysis.density, 1)} g of fiber per 1,000 calories logged. The Institute of Medicine’s targets are built on ${PER_1000_KCAL}.</p>`;
    } else if (analysis.density !== null) {
      density = `<p class="cantein-aa__plan-after">You’ve logged about ${fmtNum(Math.round(analysis.kcal / 10) * 10)} calories so far. Log the rest of your day to see your fiber per 1,000 calories.</p>`;
    }
    return panel('Where today’s fiber came from', `
      <ul class="cantein-aa__plan">${rows}</ul>
      <p class="cantein-aa__plan-after">${esc(meals)}</p>
      ${density}`);
  }

  function canteinPanel() {
    return panel('Where Cantein fits', `
      <p>One 35 g scoop of the original Cantein has 2 g of fiber, the fiber the hemp seed brings. <a href="/pages/fiber">Cantein Protein + Fiber</a> adds organic acacia for 6 g a scoop, about one weekly step on its own.</p>
      <ul>
        <li>Count a fiber scoop as one of your 5 g steps, not a freebie on top</li>
        <li>Food brings more than fiber, so let it do most of the work</li>
      </ul>
      <a href="/pages/why-cantein-fiber-stops-at-2-grams">Compare the two lines</a>`, 'cantein-aa__cantein');
  }

  function summaryText(profile, analysis) {
    if (!state.items.length) {
      return `Your target is ${fmtNum(profile.target)} g of fiber a day. Log today’s food to see where you are.`;
    }
    const base = `Fiber: ${fmtFiber(analysis.fiber)} of ${fmtNum(profile.target)} g (${fmtPct(analysis.ratio)}).`;
    if (analysis.status === 'ok') {
      return analysis.fiber >= profile.target
        ? `${base} You hit your target today.`
        : `${base} Within 1 g of your target, close enough.`;
    }
    return `${base} ${fmtFiber(analysis.deficit)} g to go, about ${Math.ceil(analysis.deficit / STEP - 1e-6)} weekly step${analysis.deficit > STEP ? 's' : ''} of 5 g.`;
  }

  function setSummary(text) {
    if (summaryEl.textContent !== text) summaryEl.textContent = text;
  }

  function resultSections(profile, analysis) {
    const sections = [targetPanel(profile, analysis)];
    if (state.items.length) {
      sections.push(
        rampPanel(profile, analysis),
        nextPanel(analysis),
        `<div class="cantein-aa__extras">${sourcesPanel(profile, analysis)}${canteinPanel()}</div>`,
      );
    }
    return sections.join('');
  }

  // Before an age is entered, show the result panels for the placeholder age (and the sample day
  // if nothing is logged) so visitors can see what they'll get.
  function previewSections() {
    const real = { age: state.age, items: state.items };
    state.age = form.elements.age.placeholder || '42';
    if (!state.items.length) state.items = sampleItems('typical', -100);
    try {
      const profile = getProfile();
      return resultSections(profile, analyze(profile));
    } finally {
      Object.assign(state, real);
    }
  }

  function update() {
    save();
    const profile = getProfile();
    if (profile.error) {
      outputEl.innerHTML = profile.missingAge
        ? `<div class="cantein-fc__preview" inert aria-hidden="true"><p class="cantein-fc__preview-label">Preview</p>${previewSections()}</div>`
        : '';
      setSummary(profile.error);
      return;
    }
    const analysis = analyze(profile);
    outputEl.innerHTML = resultSections(profile, analysis);
    setSummary(summaryText(profile, analysis));
  }

  function writeForm() {
    for (const field of FIELDS) {
      const control = form.elements[field];
      if (control) control.value = state[field];
    }
  }

  function updateSexUI() {
    $('[data-fc-female-only]').hidden = state.sex !== 'female';
  }

  function updateMealName() {
    mealNameEl.textContent = MEALS[state.meal].toLowerCase();
  }

  function refreshAll() {
    writeForm();
    updateSexUI();
    updateMealName();
    renderLog();
    update();
  }

  function onField(control) {
    const { name } = control;
    if (!FIELDS.includes(name)) return;
    if (control.type === 'radio' && !control.checked) return;
    state[name] = control.value;
    if (name === 'meal') updateMealName();
    if (name === 'sex') updateSexUI();
    update();
  }

  function itemRow(item) {
    const tail = (name) => `<span class="cantein-aa__item-protein" data-item-fiber></span>`
      + `<button type="button" class="cantein-aa__remove" data-remove aria-label="Remove ${esc(name)}">×</button>`;
    if (item.custom) {
      const name = item.name || 'Other food';
      return `<div class="cantein-aa__item" data-uid="${item.uid}">
        <span class="cantein-aa__item-name">${esc(name)}<small>Custom: enter grams of fiber</small></span>
        <input type="number" inputmode="decimal" min="0" step="any" placeholder="0" value="${esc(item.fiber)}" data-field="fiber" aria-label="Grams of fiber in ${esc(name)}">
        <select disabled aria-hidden="true"><option>g fiber</option></select>
        ${tail(name)}
      </div>`;
    }
    const food = FOOD[item.foodId];
    const units = food.s
      .map(([label, grams], i) => `<option value="${i}"${item.unit === i ? ' selected' : ''}>${esc(servingLabel(label, grams))}</option>`)
      .join('');
    return `<div class="cantein-aa__item" data-uid="${item.uid}">
      <span class="cantein-aa__item-name">${esc(food.n)}<small>${esc(food.g)}</small></span>
      <input type="number" inputmode="decimal" min="0" step="any" value="${esc(item.qty)}" data-field="qty" aria-label="Amount of ${esc(food.n)}">
      <select data-field="unit" aria-label="Unit for ${esc(food.n)}">${units}<option value="g"${item.unit === 'g' ? ' selected' : ''}>grams</option></select>
      ${tail(food.n)}
    </div>`;
  }

  function renderLog() {
    if (!state.items.length) {
      logEl.innerHTML = '<p class="cantein-aa__log-empty">No foods yet. Search above to add what you ate, or load a sample day.</p>';
      return;
    }
    logEl.innerHTML = Object.entries(MEALS).map(([key, label]) => {
      const items = state.items.filter((item) => item.meal === key);
      if (!items.length) return '';
      return `<div class="cantein-aa__meal">
        <div class="cantein-aa__meal-head"><strong>${label}</strong><span data-meal-total="${key}"></span></div>
        ${items.map(itemRow).join('')}
      </div>`;
    }).join('');
    updateLogNumbers();
  }

  function updateLogNumbers() {
    const totals = {};
    for (const item of state.items) {
      const { fiber } = nutrients(item);
      totals[item.meal] = (totals[item.meal] || 0) + fiber;
      const cell = logEl.querySelector(`[data-uid="${item.uid}"] [data-item-fiber]`);
      if (cell) cell.innerHTML = `${fmtFiber(fiber)} g<span class="visually-hidden"> fiber</span>`;
    }
    logEl.querySelectorAll('[data-meal-total]').forEach((el) => {
      el.textContent = `${fmtFiber(totals[el.dataset.mealTotal] || 0)} g fiber`;
    });
  }

  function findItem(el) {
    const row = el.closest('[data-uid]');
    return row ? [row, state.items.find((item) => item.uid === Number(row.dataset.uid))] : [];
  }

  function onLogEdit(event) {
    const [row, item] = findItem(event.target);
    const { field } = event.target.dataset;
    if (!item || !ITEM_FIELDS.includes(field)) return;
    if (field === 'unit') {
      const food = FOOD[item.foodId];
      const grams = itemGrams(item, food);
      item.unit = event.target.value === 'g' ? 'g' : Number(event.target.value);
      if (grams > 0) {
        item.qty = String(item.unit === 'g' ? Math.round(grams) : round(grams / food.s[item.unit][1], 2));
        row.querySelector('[data-field="qty"]').value = item.qty;
      }
    } else {
      item[field] = event.target.value;
    }
    updateLogNumbers();
    update();
  }

  function addFood(foodId, qty, unit = 0) {
    const food = FOOD[foodId];
    if (!food) return;
    state.items.push({ uid: nextUid++, foodId, qty: String(qty ?? food.q), unit, meal: state.meal });
    renderLog();
    update();
  }

  function addCustom(name) {
    const uid = nextUid++;
    state.items.push({ uid, custom: true, name: name.slice(0, 60), fiber: '', meal: state.meal });
    renderLog();
    update();
    logEl.querySelector(`[data-uid="${uid}"] [data-field="fiber"]`)?.focus();
  }

  const normalize = (text) => text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, ' ').trim();
  const SEARCH = FOODS.map((food) => ({
    food,
    name: normalize(food.n).split(' '),
    words: normalize(`${food.n} ${food.w || ''}`).split(' '),
    group: normalize(food.g).split(' '),
  }));

  function stems(term) {
    const forms = [term];
    if (term.length > 3 && term.endsWith('es')) forms.push(term.slice(0, -2));
    if (term.length > 3 && term.endsWith('s')) forms.push(term.slice(0, -1));
    return forms;
  }

  const UNIT_WORDS = {
    g: 'g', gram: 'g', grams: 'g', oz: 'oz', ounce: 'oz', ounces: 'oz', lb: 'lb', lbs: 'lb', pound: 'lb', pounds: 'lb', kg: 'kg',
    cup: 'cup', cups: 'cup', tbsp: 'tbsp', tablespoon: 'tbsp', tablespoons: 'tbsp', tsp: 'tsp', teaspoon: 'tsp', teaspoons: 'tsp',
    scoop: 'scoop', scoops: 'scoop', slice: 'slice', slices: 'slice', can: 'can', cans: 'can', container: 'container',
    containers: 'container', serving: 'serving', servings: 'serving', piece: 'piece', pieces: 'piece',
  };
  const GRAMS_PER_UNIT = { g: 1, oz: 28.349523125, lb: 453.59237, kg: 1000 };
  const NUMBER_WORDS = { a: 1, an: 1, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, half: 0.5 };
  const FILLER_WORDS = new Set(['of', 'x', 'small', 'medium', 'large']);
  const MAX_SERVINGS = 12;

  function readNumber(token) {
    if (/^\d+(\.\d+)?$/.test(token)) return parseFloat(token);
    const fraction = /^(\d+)\/(\d+)$/.exec(token);
    return fraction && Number(fraction[2]) ? Number(fraction[1]) / Number(fraction[2]) : null;
  }

  // Splits "1 cup raspberries" or "lentils 150g" into search terms and an amount.
  function parseQuery(query) {
    const tokens = query.toLowerCase()
      .replace(/[¼½¾]/g, (char) => ` ${{ '¼': '1/4', '½': '1/2', '¾': '3/4' }[char]} `)
      .replace(/(\d),(\d)/g, '$1.$2')
      .replace(/(\d)([a-z])/g, '$1 $2')
      .split(/[\s,]+/)
      .filter(Boolean);
    let qty = null;
    let unit = null;
    let start = 0;
    let end = tokens.length;
    const lead = readNumber(tokens[0] || '');
    if (lead !== null || (NUMBER_WORDS[tokens[0]] && tokens.length > 1)) {
      qty = lead ?? NUMBER_WORDS[tokens[0]];
      start = 1;
      const part = lead !== null && Number.isInteger(lead) && /^\d+\/\d+$/.test(tokens[1] || '') ? readNumber(tokens[1]) : null;
      if (part !== null && part < 1) {
        qty += part;
        start = 2;
      }
      if (UNIT_WORDS[tokens[start]]) unit = UNIT_WORDS[tokens[start++]];
    } else if (end > 1) {
      const lastUnit = UNIT_WORDS[tokens[end - 1]];
      const trailing = readNumber(tokens[end - (lastUnit ? 2 : 1)] || '');
      if (trailing !== null && end > (lastUnit ? 2 : 1)) {
        qty = trailing;
        unit = lastUnit || null;
        end -= lastUnit ? 2 : 1;
      }
    }
    const terms = normalize(tokens.slice(start, end).join(' ')).split(' ')
      .filter((term) => term && !FILLER_WORDS.has(term) && !UNIT_WORDS[term] && !/^\d+$/.test(term));
    return { terms, qty, unit };
  }

  // The logged amount for a parsed quantity, or null to fall back to the food's default serving.
  function amountFor(food, { qty, unit }) {
    if (!(qty > 0)) return null;
    const find = (name) => food.s.findIndex(([label]) => label.split(/[\s,(]/)[0] === name);
    if (!unit || unit === 'serving' || unit === 'piece') {
      return qty <= MAX_SERVINGS ? { qty, unit: 0 } : { qty: round(qty, 1), unit: 'g' };
    }
    if (GRAMS_PER_UNIT[unit]) {
      const oz = find('oz');
      if (unit === 'oz' && oz >= 0) return { qty, unit: oz };
      if (unit === 'lb' && oz >= 0) return { qty: qty * 16, unit: oz };
      return { qty: round(qty * GRAMS_PER_UNIT[unit], 1), unit: 'g' };
    }
    const index = find(unit);
    if (index >= 0) return { qty, unit: index };
    const tbsp = find('tbsp');
    const cup = find('cup');
    if (unit === 'tsp' && tbsp >= 0) return { qty: round(qty / 3, 2), unit: tbsp };
    if (unit === 'tbsp' && cup >= 0) return { qty: round(qty / 16, 2), unit: cup };
    if (unit === 'cup' && tbsp >= 0) return { qty: qty * 16, unit: tbsp };
    return null;
  }

  function searchFoods(terms) {
    if (!terms.length) return [];
    const results = [];
    for (const entry of SEARCH) {
      let score = 0;
      let direct = true;
      for (const term of terms) {
        const forms = stems(term);
        const starts = (words) => words.some((word) => forms.some((form) => word.startsWith(form)));
        if (entry.words.includes(term)) score += 1;
        if (starts(entry.name)) score += 3;
        else if (starts(entry.words)) score += 2;
        else if (term.length > 2 && entry.words.some((word) => forms.some((form) => word.includes(form)))) score += 1;
        else if (starts(entry.group)) {
          score += 1;
          direct = false;
        } else {
          score = 0;
          break;
        }
      }
      if (!score) continue;
      if (entry.name.join(' ').startsWith(terms.join(' '))) score += 2;
      results.push({ food: entry.food, score, direct });
    }
    const matches = results.some((result) => result.direct) ? results.filter((result) => result.direct) : results;
    return matches
      .sort((a, b) => b.score - a.score || a.food.n.length - b.food.n.length)
      .slice(0, 8)
      .map((result) => result.food);
  }

  // Anonymous, personless PostHog events: no cookies, no stored ID, no location, and nothing from the profile or log.
  function track(event, properties) {
    if (!ANALYTICS_KEY) return;
    const privacy = window.Shopify?.customerPrivacy;
    if (typeof privacy?.analyticsProcessingAllowed === 'function' && !privacy.analyticsProcessingAllowed()) return;
    fetch(`${ANALYTICS_HOST}/i/v0/e/`, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        api_key: ANALYTICS_KEY,
        event,
        distinct_id: VISIT_ID,
        properties: {
          ...properties,
          calculator: 'fiber',
          $process_person_profile: false,
          $geoip_disable: true,
          $current_url: location.origin + location.pathname,
        },
      }),
    }).catch(() => {});
  }

  const trackedMisses = new Set();
  let missTimer = 0;

  function trackMiss(query, terms) {
    clearTimeout(missTimer);
    const term = terms.join(' ');
    if (term.length < 3 || trackedMisses.has(term)) return;
    missTimer = setTimeout(() => {
      trackedMisses.add(term);
      track('food_search_no_results', { search_term: term, query: query.slice(0, 80) });
    }, MISS_DELAY);
  }

  function openOptions() {
    const query = searchEl.value.trim();
    if (!query) {
      clearTimeout(missTimer);
      closeOptions();
      return;
    }
    const parsed = parseQuery(query);
    const foods = searchFoods(parsed.terms);
    if (foods.length) clearTimeout(missTimer);
    else trackMiss(query, parsed.terms);
    options = [...foods.map((food) => ({ food, amount: amountFor(food, parsed) })), { custom: query }];
    optionsEl.innerHTML = options.map((option, i) => {
      if (!option.food) {
        return `<li id="fc-option-${i}" role="option" aria-selected="false" data-index="${i}" class="cantein-aa__option-custom">`
          + `<strong>${foods.length ? 'Not listed?' : 'No match.'} Add “${esc(option.custom)}” by grams of fiber</strong>`
          + '<small>Use the fiber number from its nutrition label</small></li>';
      }
      const { food, amount } = option;
      const [label, size] = food.s[0];
      const grams = amount ? itemGrams(amount, food) : size;
      const detail = amount ? amountLabel(food, amount) : `per ${servingLabel(label, size)}`;
      return `<li id="fc-option-${i}" role="option" aria-selected="false" data-index="${i}">`
        + `<strong>${esc(food.n)}</strong><small>${esc(food.g)} · ${esc(detail)}</small>`
        + `<span class="cantein-aa__option-meta">${fmtFiber((food.f * grams) / 100)} g fiber</span></li>`;
    }).join('');
    optionsEl.hidden = false;
    searchEl.setAttribute('aria-expanded', 'true');
    setActive(foods.length ? 0 : -1);
  }

  function closeOptions() {
    optionsEl.hidden = true;
    searchEl.setAttribute('aria-expanded', 'false');
    searchEl.removeAttribute('aria-activedescendant');
    activeOption = -1;
  }

  function setActive(index) {
    activeOption = index;
    Array.from(optionsEl.children).forEach((el, i) => el.setAttribute('aria-selected', String(i === index)));
    const active = optionsEl.children[index];
    if (active) {
      searchEl.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    } else {
      searchEl.removeAttribute('aria-activedescendant');
    }
  }

  function choose(index) {
    const option = options[index];
    if (!option) return;
    searchEl.value = '';
    closeOptions();
    if (option.food) {
      addFood(option.food.id, option.amount?.qty, option.amount?.unit ?? 0);
      searchEl.focus();
    } else {
      clearTimeout(missTimer);
      track('custom_food_added', {
        search_term: parseQuery(option.custom).terms.join(' '),
        query: option.custom.slice(0, 80),
        had_matches: options.length > 1,
      });
      addCustom(option.custom);
    }
  }

  function loadSample(name) {
    const sample = SAMPLES[name];
    if (!sample) return;
    if (getProfile().error) Object.assign(state, sample.profile, { stage: 'none' });
    state.items = sampleItems(name, nextUid);
    nextUid += state.items.length;
    refreshAll();
  }

  form.addEventListener('input', (event) => {
    if (event.target.type !== 'radio') onField(event.target);
  });
  form.addEventListener('change', (event) => {
    if (event.target.type === 'radio') onField(event.target);
  });
  form.addEventListener('submit', (event) => event.preventDefault());

  logEl.addEventListener('input', (event) => {
    if (event.target.tagName === 'INPUT') onLogEdit(event);
  });
  logEl.addEventListener('change', (event) => {
    if (event.target.tagName === 'SELECT') onLogEdit(event);
  });
  logEl.addEventListener('click', (event) => {
    const button = event.target.closest('[data-remove]');
    if (!button) return;
    const [, item] = findItem(button);
    state.items = state.items.filter((entry) => entry !== item);
    renderLog();
    update();
    searchEl.focus();
  });

  searchEl.addEventListener('input', openOptions);
  searchEl.addEventListener('focus', () => {
    if (searchEl.value.trim()) openOptions();
  });
  searchEl.addEventListener('blur', () => setTimeout(closeOptions, 120));
  searchEl.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (optionsEl.hidden) {
        openOptions();
        return;
      }
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActive((activeOption + step + options.length) % options.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (!optionsEl.hidden && options.length) choose(activeOption >= 0 ? activeOption : 0);
    } else if (event.key === 'Escape' && !optionsEl.hidden) {
      event.preventDefault();
      closeOptions();
    }
  });
  optionsEl.addEventListener('mousedown', (event) => event.preventDefault());
  optionsEl.addEventListener('click', (event) => {
    const option = event.target.closest('[role="option"]');
    if (option) choose(Number(option.dataset.index));
  });

  outputEl.addEventListener('click', (event) => {
    const button = event.target.closest('[data-add-food]');
    if (!button) return;
    const food = FOOD[button.dataset.addFood];
    const grams = Number(button.dataset.grams);
    if (food && grams > 0) addFood(food.id, round(grams / food.s[0][1], 2));
  });

  app.querySelectorAll('[data-fc-sample]').forEach((button) => {
    button.addEventListener('click', () => loadSample(button.dataset.fcSample));
  });
  $('[data-fc-reset]').addEventListener('click', () => {
    state = { ...DEFAULTS, items: [] };
    refreshAll();
  });


  refreshAll();
})();
